export type StockImportMode = 'RECEIPT' | 'INVENTORY';

export type StockField =
  | 'sku'
  | 'ean'
  | 'manufacturerRef'
  | 'brand'
  | 'quantity'
  | 'docRef'
  | 'note'
  | 'description'
  | 'unitPrice';
export type IdentifierField = 'sku' | 'ean' | 'manufacturerRef';

export const IDENTIFIER_FIELDS: IdentifierField[] = ['sku', 'ean', 'manufacturerRef'];
export const STOCK_FIELDS: StockField[] = [
  'sku',
  'ean',
  'manufacturerRef',
  'brand',
  'quantity',
  'docRef',
  'note',
  'description',
  'unitPrice',
];

/** field → 0-based column index, null = not taken from the file. */
export type Mapping = Record<StockField, number | null>;

/** Why a field points at its column — drives the chip next to it. */
export type MappingSource = 'HEADER' | 'VALUES' | 'PATTERN' | 'REMEMBERED' | 'MANUAL';

export interface AnalyzeColumn {
  index: number;
  letter: string;
  header: string;
  samples: string[];
  filled: number;
  matchRates: Partial<Record<IdentifierField, number>>;
}

export interface FieldSuggestion {
  column: number;
  confidence: number;
  reason: 'HEADER' | 'VALUES' | 'PATTERN';
  matchRate?: number;
}

export interface AnalyzeResponse {
  sheets: string[];
  sheet: string | null;
  headerRow: number;
  head: string[][];
  totalRows: number;
  columns: AnalyzeColumn[];
  suggestions: Partial<Record<StockField, FieldSuggestion>>;
  mapping: Partial<Mapping>;
  /** Movement type the quantity header implies ("Réel" → an inventory count). */
  suggestedMode: StockImportMode | null;
}

/** CREATE — not in the catalogue: the product is created at commit (from TecDoc or by hand).
 *  INVOICE_ONLY — purchase invoice: already received at Receptions; invoiced and costed, no stock moves. */
export type StockRowStatus =
  | 'RECEIPT'
  | 'ADJUST'
  | 'NO_CHANGE'
  | 'NOT_FOUND'
  | 'ERROR'
  | 'CREATE'
  | 'INVOICE_ONLY';

/** Purchase invoice: how the line meets the supplier's purchase orders and the cost (migration 147). */
export interface PurchaseRowInfo {
  alreadyInStock: number;
  receivedOnPo: number;
  free: number;
  poNumbers: string[];
  referencePrice: number | null;
  referenceSource: 'PO' | 'COST' | null;
  variancePct: number | null;
  varianceFlag: boolean;
  priceAccepted: boolean;
  costModel: 'WAP' | 'TARIFF_DISCOUNT' | 'NONE';
  currentCost: number | null;
}

export interface ProductCandidate {
  id: string;
  sku: string;
  name: string;
  brand: string | null;
}

export interface TecDocCandidate {
  artId: number;
  brand: string;
  articleNr: string;
}

export interface CreateSpec {
  name: string;
  brand: string | null;
  manufacturerRef: string;
  purchasePriceHT: number | null;
  tecdocArtId: number | null;
}

/** The operator's decision for one line, keyed by file line number in the layout. */
export type RowResolution =
  | { action: 'LINK'; productId: string }
  | {
      action: 'CREATE';
      name: string;
      brand?: string | null;
      manufacturerRef?: string | null;
      purchasePriceHT?: number | null;
      tecdocArtId?: number | null;
    };

export interface StockPreviewRow {
  line: number;
  ref: string;
  status: StockRowStatus;
  message?: string;
  matchedBy?: 'SKU' | 'EAN' | 'MANUFACTURER_REF' | 'REFERENCE' | 'MANUAL';
  productSku?: string;
  productName?: string;
  brand?: string | null;
  fileBrand?: string | null;
  description?: string;
  unitPrice?: number;
  current?: number | null;
  quantity?: number;
  movement?: number;
  resulting?: number;
  candidates?: ProductCandidate[];
  tecdoc?: TecDocCandidate[];
  resolvedBy?: 'OPERATOR' | 'TECDOC';
  create?: CreateSpec;
  purchase?: PurchaseRowInfo;
}

export interface StockPreviewResponse {
  mode: StockImportMode;
  total: number;
  summary: Record<
    'receipt' | 'adjust' | 'no_change' | 'not_found' | 'error' | 'create' | 'invoice_only',
    number
  >;
  ignoredColumns: string[];
  rows: StockPreviewRow[];
  /** Purchase import: this supplier's invoice number was already received. */
  alreadyRecorded?: {
    id: string;
    invoiceNumber: string;
    createdAt: string;
    lineCount: number;
  } | null;
  /** Purchase import: file lines priced > 5 % off the order / current cost — to accept by name. */
  priceVariances?: number[];
}

export interface StockImportResult {
  total: number;
  applied: number;
  created: number;
  skipped: number;
  failed: number;
  errors: { row: number; ref: string; message: string }[];
  purchaseInvoiceId?: string | null;
  /** Purchase import: units invoiced that were already in stock; products whose cost moved. */
  alreadyInStock?: number;
  costUpdates?: number;
}

export const emptyMapping = (): Mapping => ({
  sku: null,
  ean: null,
  manufacturerRef: null,
  brand: null,
  quantity: null,
  docRef: null,
  note: null,
  description: null,
  unitPrice: null,
});

/** What Claude read off an invoice PDF (the lines themselves come back as a CSV file). */
export interface InvoiceSummary {
  documentType: 'INVOICE' | 'CREDIT_NOTE' | 'DELIVERY_NOTE' | 'OTHER';
  documentNumber: string | null;
  documentDate: string | null;
  supplierName: string | null;
  currency: string | null;
  lineCount: number;
  creditedLines: number;
  engine: string;
}

export type InvoiceJob =
  | { id: string; status: 'PROCESSING'; filename: string; startedAt: string }
  | {
      id: string;
      status: 'DONE';
      filename: string;
      startedAt: string;
      invoice: InvoiceSummary;
      file: { filename: string; fileBase64: string };
    }
  | { id: string; status: 'FAILED'; filename: string; startedAt: string; error: string };
